"use client";;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:ENCON_SENTRY_KEY":true});}catch(e){}}();

import { usePathname, useRouter } from "@/i18n/routing";
import type { Tag } from "@/payload-types";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

export type FilterOption = {
  id: string;
  label: string;
  checked: boolean;
};

const DEBOUNCE_TIME = 200;

type UseArchiveFilterParamsOptions = {
  solutionTags: Tag[];
  industryTags: Tag[];
  frameworkTags: Tag[];
  onFilterChange?: (
    filters: Record<string, string[]>,
    searchQuery?: string,
    options?: { skipScroll?: boolean },
  ) => void;
  onIndustriesChange?: (industries: string[]) => void;
};

const FILTER_PARAM_KEYS = ["solutions", "industries", "framework"] as const;
const PAGE_PARAM = "page";
const ARCHIVE_FILTER_STORAGE_PREFIX = "archive-filter-query:";

function archiveFilterStorageKey(pathname: string) {
  return `${ARCHIVE_FILTER_STORAGE_PREFIX}${pathname}`;
}

export function saveArchiveFilterQuery(pathname: string, query: string) {
  if (typeof window === "undefined") return;

  try {
    const key = archiveFilterStorageKey(pathname);
    if (query) {
      sessionStorage.setItem(key, query);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // sessionStorage can throw in private mode
  }
}

export function getArchiveHref(pathname: string): string {
  if (typeof window === "undefined") return pathname;

  try {
    const query = sessionStorage.getItem(archiveFilterStorageKey(pathname));
    return query ? `${pathname}?${query}` : pathname;
  } catch {
    return pathname;
  }
}

export function parsePageParam(value: string | null): number {
  const page = Number(value);
  return Number.isInteger(page) && page > 1 ? page : 1;
}

function parseIdsParam(value: string | null): string[] {
  if (!value) return [];
  return value.split(",").filter(Boolean);
}

function mapTagsToOptions(
  tags: Tag[],
  selectedIds: string[] = [],
): FilterOption[] {
  return tags.map((tag) => ({
    id: tag.id,
    label: tag.title ?? "",
    checked: selectedIds.includes(tag.id),
  }));
}

function getActiveFilters(
  filters: Record<string, FilterOption[]>,
): Record<string, string[]> {
  const activeFilters: Record<string, string[]> = {};
  for (const [key, options] of Object.entries(filters)) {
    activeFilters[key] = options
      .filter((option) => option.checked)
      .map((option) => option.id);
  }
  return activeFilters;
}

function buildFilterQueryString(
  activeFilters: Record<string, string[]>,
  searchValue: string,
  currentParams: URLSearchParams,
  options?: { resetPage?: boolean },
): string {
  const params = new URLSearchParams(currentParams.toString());

  for (const key of FILTER_PARAM_KEYS) {
    params.delete(key);
  }
  params.delete("q");
  if (options?.resetPage) {
    params.delete(PAGE_PARAM);
  }

  for (const key of FILTER_PARAM_KEYS) {
    const ids = activeFilters[key];
    if (ids?.length) {
      params.set(key, ids.join(","));
    }
  }

  const trimmedSearch = searchValue.trim();
  if (trimmedSearch) {
    params.set("q", trimmedSearch);
  }

  return params.toString();
}

export function useArchiveFilterParams({
  solutionTags,
  industryTags,
  frameworkTags,
  onFilterChange,
  onIndustriesChange,
}: UseArchiveFilterParamsOptions) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedFromParams = useRef(false);

  const [filters, setFilters] = useState<Record<string, FilterOption[]>>(
    () => ({
      solutions: mapTagsToOptions(
        solutionTags,
        parseIdsParam(searchParams.get("solutions")),
      ),
      industries: mapTagsToOptions(
        industryTags,
        parseIdsParam(searchParams.get("industries")),
      ),
      framework: mapTagsToOptions(
        frameworkTags,
        parseIdsParam(searchParams.get("framework")),
      ),
    }),
  );
  const [search, setSearch] = useState(() => searchParams.get("q") ?? "");

  const syncQueryParams = useCallback(
    (activeFilters: Record<string, string[]>, searchValue: string) => {
      const query = buildFilterQueryString(
        activeFilters,
        searchValue,
        searchParams,
        { resetPage: true },
      );
      const nextUrl = query ? `${pathname}?${query}` : pathname;
      saveArchiveFilterQuery(pathname, query);

      if (query === searchParams.toString()) return;

      router.replace(nextUrl, { scroll: false });
    },
    [pathname, router, searchParams],
  );

  const debouncedFilterChange = useCallback(
    (activeFilters: Record<string, string[]>, searchValue: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        syncQueryParams(activeFilters, searchValue);
        if (onFilterChange) {
          onFilterChange(activeFilters, searchValue);
        }
      }, DEBOUNCE_TIME);
    },
    [onFilterChange, syncQueryParams],
  );

  useEffect(() => {
    if (hasInitializedFromParams.current) return;
    hasInitializedFromParams.current = true;

    const activeFilters = getActiveFilters(filters);
    saveArchiveFilterQuery(
      pathname,
      buildFilterQueryString(activeFilters, search, searchParams),
    );

    if (onIndustriesChange) {
      onIndustriesChange(activeFilters.industries ?? []);
    }
    if (onFilterChange) {
      onFilterChange(activeFilters, search, { skipScroll: true });
    }
  }, [filters, search, onFilterChange, onIndustriesChange, pathname, searchParams]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleCheckboxChange = (category: string, optionId: string) => {
    const updatedFilters = {
      ...filters,
      [category]: filters[category].map((option) =>
        option.id === optionId
          ? { ...option, checked: !option.checked }
          : option,
      ),
    };

    setFilters(updatedFilters);

    const activeFilters = getActiveFilters(updatedFilters);

    if (category === "industries" && onIndustriesChange) {
      onIndustriesChange(activeFilters.industries ?? []);
    }

    syncQueryParams(activeFilters, search);
    if (onFilterChange) {
      onFilterChange(activeFilters, search);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);

    const activeFilters = getActiveFilters(filters);

    debouncedFilterChange(activeFilters, value);
  };

  const clearFilters = () => {
    const clearedFilters = Object.entries(filters).reduce(
      (acc, [key, options]) => {
        acc[key] = options.map((option) => ({ ...option, checked: false }));
        return acc;
      },
      {} as Record<string, FilterOption[]>,
    );

    setFilters(clearedFilters);
    setSearch("");
    syncQueryParams({}, "");
    if (onIndustriesChange) {
      onIndustriesChange([]);
    }
    if (onFilterChange) {
      onFilterChange({}, "");
    }
  };

  return {
    filters,
    search,
    handleCheckboxChange,
    handleSearchChange,
    clearFilters,
  };
}
